import * as React from 'react';
import { initialInquiry, InquiryFormInterface } from '../InquiryForm';
import { CartItem, CheckoutVenueRecommendationsCardInterface } from '@weddingspot/ws-core-components';

export enum Steps {
    FirstStep = 1,
    MoreVenues = 2,
    HotelsRecommendations = 3,
    Final = 4,
}

interface CheckoutContextInterface {
    step: Steps;
    confirmButtonAction: () => void;
    setConfirmButtonAction: (value: () => void) => void;
    isConfirmDisabled: boolean;
    setIsConfirmDisabled: (value: boolean) => void;
    secondaryButtonAction: () => void;
    setSecondaryButtonAction: (value: () => void) => void;
    confirmButtonName: string;
    setConfirmButtonName: (value: string) => void;
    secondaryButtonName: string;
    setSecondaryButtonName: (value: string) => void;
    setStep: (value: Steps) => void;
    submittedInquiryData: InquiryFormInterface;
    setSubmittedInquiryData: (value: InquiryFormInterface) => void;
    isRequested: boolean;
    setIsRequested: (value: boolean) => void;
    // using two different sets of hotels
    // cards - for cards only
    // items can contain more information and play like a local shoppingCard items
    recommendationCards: CheckoutVenueRecommendationsCardInterface[];
    hotelRecommendations: CartItem[];
    setRecommendationCards: (value: CheckoutVenueRecommendationsCardInterface[]) => void;
    setHotelRecommendations: (value: CartItem[]) => void;
    hotelRecommendationCards: CheckoutVenueRecommendationsCardInterface[];
    setHotelRecommendationCards: (value: CheckoutVenueRecommendationsCardInterface[]) => void;
    finalMarket?: string;
    setFinalMarket: (market?: string) => void;
    RFPFormElement: HTMLFormElement | null;
    setRFPFormElement: (el: HTMLFormElement | null) => void;
    moreVenuesSanded: boolean;
    setMoreVenuesSanded: (sanded: boolean) => void;
}

export const CheckoutContext = React.createContext<CheckoutContextInterface>({
    step: Steps.FirstStep,
    confirmButtonAction: () => {},
    setConfirmButtonAction: () => {},
    secondaryButtonAction: () => {},
    setSecondaryButtonAction: () => {},
    confirmButtonName: '',
    setConfirmButtonName: () => {},
    isConfirmDisabled: false,
    setIsConfirmDisabled: () => {},
    secondaryButtonName: '',
    setSecondaryButtonName: () => {},
    setStep: () => {},
    submittedInquiryData: initialInquiry,
    setSubmittedInquiryData: () => {},
    isRequested: false,
    setIsRequested: () => {},
    recommendationCards: [],
    setRecommendationCards: () => {},
    hotelRecommendations: [],
    setHotelRecommendations: () => {},
    hotelRecommendationCards: [],
    setHotelRecommendationCards: () => {},
    finalMarket: '',
    setFinalMarket: () => {},
    RFPFormElement: null,
    setRFPFormElement: () => {},
    moreVenuesSanded: false,
    setMoreVenuesSanded: () => {},
});

export const CheckoutContextProvider = ({ children }: { children: React.ReactNode }) => {
    const [step, setStep] = React.useState<Steps>(Steps.FirstStep);
    const [confirmButtonAction, setConfirmButtonActionHook] = React.useState<() => void>(() => {});
    const [confirmButtonName, setConfirmButtonName] = React.useState<string>('');
    const [secondaryButtonName, setSecondaryButtonName] = React.useState<string>('Cancel');
    const [secondaryButtonAction, setSecondaryButtonActionHook] = React.useState<() => void>(() => {});
    const [submittedInquiryData, setSubmittedInquiryData] = React.useState<InquiryFormInterface>(initialInquiry);
    const [isRequested, setIsRequested] = React.useState<boolean>(false);
    const [recommendationCards, setRecommendationCards] = React.useState<CheckoutVenueRecommendationsCardInterface[]>([]);
    const [hotelRecommendations, setHotelRecommendations] = React.useState<CartItem[]>([]);
    const [hotelRecommendationCards, setHotelRecommendationCards] = React.useState<CheckoutVenueRecommendationsCardInterface[]>([]);
    const [finalMarket, setFinalMarket] = React.useState<string>();
    const [RFPFormElement, setRFPFormElement] = React.useState<HTMLFormElement | null>(null);
    const [moreVenuesSanded, setMoreVenuesSanded] = React.useState(false);
    const [isConfirmDisabled, setIsConfirmDisabled] = React.useState(false);

    const setConfirmButtonAction = (func: () => void) => {
        setConfirmButtonActionHook(() => func);
    };

    const setSecondaryButtonAction = (func: () => void) => {
        setSecondaryButtonActionHook(() => func);
    };

    return (
        <CheckoutContext.Provider
            value={{
                step,
                setStep,
                confirmButtonAction,
                setConfirmButtonAction,
                confirmButtonName,
                setConfirmButtonName,
                isConfirmDisabled,
                setIsConfirmDisabled,
                secondaryButtonName,
                setSecondaryButtonName,
                secondaryButtonAction,
                setSecondaryButtonAction,
                submittedInquiryData,
                setSubmittedInquiryData,
                isRequested,
                setIsRequested,
                recommendationCards,
                setRecommendationCards,
                hotelRecommendations,
                setHotelRecommendations,
                hotelRecommendationCards,
                setHotelRecommendationCards,
                finalMarket,
                setFinalMarket,
                RFPFormElement,
                setRFPFormElement,
                moreVenuesSanded,
                setMoreVenuesSanded,
            }}
        >
            {children}
        </CheckoutContext.Provider>
    );
};
